import { createStore, Store } from 'vuex';
import VuexPersistence from 'vuex-persist';
import { cookies } from '@/utilities/cookies';
import { woTemplateModule } from './modules/data/woTemplate';
import { siteOrdersModule } from './modules/data/siteOrders';

import accessControl from './modules/accessControl';
import auth from './modules/auth';
import lang from './modules/lang';
import modalTab from './modules/modalTab';
import navigationModule from './modules/navigation';
import breadcrumbModule from './modules/breadcrumb';
import member from './modules/member';
import user from './modules/user';
import massBalanceModule from './modules/massBalance';
import notificationsModule from './modules/notifications';
import site from './modules/data/site';
import team from './modules/data/team';
import { productModule } from './modules/data/product';
import { orderModule } from './modules/data/order';
import workOrder from './modules/data/workOrder';
import clientModule from '@/store/modules/data/client';
import component from '@/store/modules/data/component';
import analysisType from '@/store/modules/data/analysisType';
import network from './modules/data/network';
import type { RootState } from '@/interfaces/store';
import copySite from './modules/data/copySite';
import controlChart from './modules/data/controlChart';
import graphics from './graphics';
import { crmStoreModules } from '@/crm/src';

/**
 * Vuex mutation to restore state from persisted storage.
 * @param {RootState} state - Vuex state.
 * @param {Partial<RootState>} payload - Payload to restore.
 */
const RESTORE_MUTATION = (state: RootState, payload: Partial<RootState>) => {
    Object.assign(state, payload);
};

/**
 * Cookie-based storage adapter for cross-subdomain auth sharing.
 * Uses shared domain cookies so auth persists across subdomains.
 */
const cookieStorage: Storage = {
    get length() {
        return cookies.keys().length;
    },
    key(index: number) {
        return cookies.keys()[index] ?? null;
    },
    getItem(key: string) {
        return cookies.get(key) ?? null;
    },
    setItem(key: string, value: string) {
        cookies.set(key, value, 7, true); // 7 days, shared domain
    },
    removeItem(key: string) {
        cookies.remove(key, true); // shared domain
    },
    clear() {
        cookies.keys().forEach((key) => cookies.remove(key, true));
    },
};

const vuexPersist = new VuexPersistence({
    key: 'aquassist',
    storage: cookieStorage,
    modules: ['auth', 'accessControl', 'contracts', 'team'],
    restoreState: (key) => {
        try {
            const data = cookies.get(key);
            return data ? JSON.parse(data) : {};
        } catch (err) {
            console.error('Error restoring state from cookie:', err);
            return {};
        }
    },
});

let resolveRestored: () => void;
const restored = new Promise<void>((resolve) => {
    resolveRestored = resolve;
});

const storeInstance: Store<RootState> = createStore<RootState>({
    strict: true,
    modules: {
        accessControl,
        auth,
        lang,
        modalTab,
        navigation: navigationModule,
        breadcrumb: breadcrumbModule,
        woTemplate: woTemplateModule,
        workOrder,
        order: orderModule,
        product: productModule,
        client: clientModule,
        member,
        user,
        massBalance: massBalanceModule,
        site,
        component,
        analysisType,
        team,
        network,
        copySite,
        controlChart,
        siteOrders: siteOrdersModule,
        graphics,
        notifications: notificationsModule,
        ...crmStoreModules,
    },
    mutations: {
        RESTORE_MUTATION,
    },
    plugins: [
        (s: Store<RootState>) => {
            vuexPersist.plugin(s);
            resolveRestored();
        },
    ],
});

// Extend the store type to include the restored property
export interface ExtendedStoreType extends Store<RootState> {
    restored: Promise<void>;
}

const typedStore = storeInstance as ExtendedStoreType;
typedStore.restored = restored;

export const getStoreInstance = () => {
    return typedStore;
};

export default typedStore;
